import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { X, Heart, Users, Shield, HandHeart, Waves, Calendar, Globe, BookOpen, Phone, FolderOpen, Compass, Link } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Services = () => {
  const navigate = useNavigate();
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});
  const [isSafeSpaceModalOpen, setIsSafeSpaceModalOpen] = useState(false);
  const [isIWCModalOpen, setIsIWCModalOpen] = useState(false);
  const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observe all sections
    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const handleSafeSpaceClick = () => {
    setIsSafeSpaceModalOpen(true);
  };

  const closeSafeSpaceModal = () => {
    setIsSafeSpaceModalOpen(false);
  };

  const handleIWCClick = () => {
    setIsIWCModalOpen(true);
  };

  const closeIWCModal = () => {
    setIsIWCModalOpen(false);
  };

  const handleResourceClick = () => {
    setIsResourceModalOpen(true);
  };

  const closeResourceModal = () => {
    setIsResourceModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Services Section */}
      <section 
        id="services"
        ref={el => sectionRefs.current.services = el}
        className={`py-20 bg-stone-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('services') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
          </div>
          
          <div className="space-y-20">
            {/* Traditional Therapy */}
            <div 
              id="traditional-therapy"
              ref={el => sectionRefs.current['traditional-therapy'] = el}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center transition-all duration-1500 delay-200 ${
                visibleSections.has('traditional-therapy')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1573497491208-6b1acb260507?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHx0aGVyYXB5fGVufDB8fHx8MTc1OTUxMDUwMHww&ixlib=rb-4.1.0&q=85"
                  alt="Traditional Therapy"
                  className="w-full h-96 object-cover rounded-2xl shadow-lg"
                />
              </div>
              <div className="space-y-4 text-center">
                <div>
                  <h3 className="text-xs text-gray-500 mb-1">THERAPY-BASED APPROACH</h3>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Therapy-Based 1:1 Traditional Therapy</h4>
                </div>
                <p className="text-gray-600 leading-relaxed text-sm max-w-xs mx-auto">
                  Insight is only powerful when it leads to action. Our process integrates proven psychological frameworks with practical application to deliver measurable outcomes. We go beyond identifying obstacles—we work with you to understand their root causes, reframe limiting patterns, and develop strategies that drive lasting change.
                </p>
                <button 
                  onClick={() => navigate('/contact')}
                  className="px-4 py-2 border rounded-full text-sm transition-colors duration-200" 
                  style={{ 
                    borderColor: '#6a6acb', 
                    color: '#6a6acb',
                    backgroundColor: 'transparent'
                  }} 
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#6a6acb';
                    e.target.style.color = 'white';
                  }} 
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#6a6acb';
                  }}
                >
                  Learn More →
                </button>
              </div>
            </div>

            {/* One-on-One Coaching */}
            <div 
              id="one-on-one-coaching"
              ref={el => sectionRefs.current['one-on-one-coaching'] = el}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center transition-all duration-1500 delay-200 ${
                visibleSections.has('one-on-one-coaching')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="lg:order-2">
                <img 
                  src="https://images.unsplash.com/photo-1636986905406-758b0e280f49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHx3b21lbiUyMGVtcG93ZXJtZW50fGVufDB8fHx8MTc1OTUxMDUxMXww&ixlib=rb-4.1.0&q=85"
                  alt="One-on-One Coaching"
                  className="w-full h-96 object-cover rounded-2xl shadow-lg"
                />
              </div>
              <div className="lg:order-1 space-y-4 text-center">
                <div>
                  <h3 className="text-xs text-gray-500 mb-1">FOCUSED SUPPORT</h3>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">One-on-One Coaching sessions</h4>
                </div>
                <p className="text-gray-600 leading-relaxed text-sm max-w-xs mx-auto">
                  Effective growth requires focus, privacy, and accountability. Our one-on-one sessions provide a confidential space tailored entirely to your needs—whether you're navigating a critical decision, building professional direction, or stabilizing personal challenges. Using structured methodologies, we help you clarify priorities, create a roadmap, and stay on track with consistent follow-through. Every session balances reflection with action, so you leave with more than insight—you leave with a plan you can implement immediately, supported by expert guidance every step of the way.
                </p>
                <button 
                  onClick={() => navigate('/contact')}
                  className="px-4 py-2 border rounded-full text-sm transition-colors duration-200" 
                  style={{ 
                    borderColor: '#6a6acb', 
                    color: '#6a6acb',
                    backgroundColor: 'transparent'
                  }} 
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#6a6acb';
                    e.target.style.color = 'white';
                  }} 
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#6a6acb';
                  }}
                >
                  Learn More →
                </button>
              </div>
            </div>

            {/* The Safe Space */}
            <div 
              id="safe-space"
              ref={el => sectionRefs.current['safe-space'] = el}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center transition-all duration-1500 delay-200 ${
                visibleSections.has('safe-space')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1500904156668-758cff89dcff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxoZWFsaW5nfGVufDB8fHx8MTc1OTUxMDUwNnww&ixlib=rb-4.1.0&q=85"
                  alt="The Safe Space"
                  className="w-full h-96 object-cover rounded-2xl shadow-lg"
                />
              </div>
              <div className="space-y-4 text-center">
                <div>
                  <h3 className="text-xs text-gray-500 mb-1">COMMUNITY SUPPORT</h3>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">The Safe Space (Pro-Bono)</h4>
                </div>
                <p className="text-gray-600 leading-relaxed text-sm max-w-xs mx-auto">
                  For women facing abuse or violence, safety and recovery shouldn't depend on privilege. The Safe Space is a private, pro bono program offering free, confidential support for those in crisis. It provides structured pathways to healing—empowerment workshops to rebuild confidence, actionable safety planning, and direct connections to trusted professionals and community resources. This is a space where dignity is preserved, privacy is paramount, and tangible steps toward a safer future are within reach.
                </p>
                <button 
                  onClick={handleSafeSpaceClick}
                  className="px-4 py-2 border rounded-full text-sm transition-colors duration-200" 
                  style={{ 
                    borderColor: '#6a6acb', 
                    color: '#6a6acb',
                    backgroundColor: 'transparent'
                  }} 
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#6a6acb';
                    e.target.style.color = 'white';
                  }} 
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#6a6acb';
                  }}
                >
                  Learn More →
                </button>
              </div>
            </div>

            {/* The International Women's Connection (IWC) Membership */}
            <div 
              id="iwc-membership"
              ref={el => sectionRefs.current['iwc-membership'] = el}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center transition-all duration-1500 delay-200 ${
                visibleSections.has('iwc-membership')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="lg:order-2">
                <img 
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHx3b21lbiUyMGNvbW11bml0eXxlbnwwfHx8fDE3NTk1MTA1MDh8MA&ixlib=rb-4.1.0&q=85"
                  alt="International Women's Connection"
                  className="w-full h-96 object-cover rounded-2xl shadow-lg"
                />
              </div>
              <div className="lg:order-1 space-y-4 text-center">
                <div>
                  <h3 className="text-xs text-gray-500 mb-1">GLOBAL COMMUNITY</h3>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">The International Women's Connection (IWC) Membership</h4>
                </div>
                <p className="text-gray-600 leading-relaxed text-sm max-w-xs mx-auto">
                  A private global community designed for women navigating transitions. Where you can access curated experiences.
                </p>
                <button 
                  onClick={handleIWCClick}
                  className="px-4 py-2 border rounded-full text-sm transition-colors duration-200" 
                  style={{ 
                    borderColor: '#6a6acb', 
                    color: '#6a6acb',
                    backgroundColor: 'transparent'
                  }} 
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#6a6acb';
                    e.target.style.color = 'white';
                  }} 
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#6a6acb';
                  }}
                >
                  Learn More →
                </button>
              </div>
            </div>

            {/* Resource Library */}
            <div 
              id="resource-library"
              ref={el => sectionRefs.current['resource-library'] = el}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center transition-all duration-1500 delay-200 ${
                visibleSections.has('resource-library')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxsaWJyYXJ5fGVufDB8fHx8MTc1OTUxMDUwOXww&ixlib=rb-4.1.0&q=85"
                  alt="Resource Library"
                  className="w-full h-96 object-cover rounded-2xl shadow-lg"
                />
              </div>
              <div className="space-y-4 text-center">
                <div>
                  <h3 className="text-xs text-gray-500 mb-1">KNOWLEDGE BASE</h3>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Resource Library</h4>
                </div>
                <p className="text-gray-600 leading-relaxed text-sm max-w-xs mx-auto">
                  A place to find what you need, when you need it.
                </p>
                <button 
                  onClick={handleResourceClick}
                  className="px-4 py-2 border rounded-full text-sm transition-colors duration-200" 
                  style={{ 
                    borderColor: '#6a6acb', 
                    color: '#6a6acb',
                    backgroundColor: 'transparent'
                  }} 
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#6a6acb';
                    e.target.style.color = 'white';
                  }} 
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#6a6acb';
                  }}
                >
                  Learn More →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        id="cta"
        ref={el => sectionRefs.current.cta = el}
        className={`bg-stone-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('cta') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="w-full h-px bg-black"></div>
        <div className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Ready to Begin Your <span className="relative inline-block">
                <span className="relative z-10">Transformation</span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-200 to-purple-200 opacity-30 rounded-lg transform -rotate-1"></span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-100 to-purple-100 opacity-50 rounded-lg transform rotate-1"></span>
              </span>?
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Join our global community of empowered women and professionals. Your journey to healing, growth, and connection starts today.
            </p>
            <button 
              onClick={() => navigate('/contact')}
              className="px-4 py-2 border rounded-full text-sm transition-colors duration-200" 
              style={{ 
                borderColor: '#6a6acb', 
                color: '#6a6acb',
                backgroundColor: 'transparent'
              }} 
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#6a6acb';
                e.target.style.color = 'white';
              }} 
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6a6acb';
              }}
            >
              Get Started Today →
            </button>
          </div>
        </div>
        <div className="w-full h-px bg-black"></div>
      </section>

      {/* Safe Space Modal */}
      <Dialog open={isSafeSpaceModalOpen} onOpenChange={setIsSafeSpaceModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              The Safe Space (Pro-Bono)
            </DialogTitle>
            <p className="text-lg font-semibold" style={{ color: 'rgba(127, 81, 244, 1)' }}>
              COMMUNITY SUPPORT
            </p>
          </DialogHeader>
          
          <div className="mt-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <p className="text-xl text-gray-700 leading-relaxed mb-6">
                  No woman should have to face violence alone.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  The Safe Space is here for you—a confidential, free group designed to support women in moments of crisis. It's a place where you can exhale, be heard, and begin to heal.
                </p>
                  </div>
              
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-8 rounded-2xl">
                <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                  Together, we create an environment of care and dignity. In this space, you'll find:
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center mb-4">
                      <Heart className="w-6 h-6 text-purple-600 mr-4" />
                      <h4 className="text-lg font-semibold text-gray-900">Understanding Hearts</h4>
                    </div>
                    <p className="text-gray-600">Listen without judgment and provide compassionate support.</p>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center mb-4">
                      <Users className="w-6 h-6 text-purple-600 mr-4" />
                      <h4 className="text-lg font-semibold text-gray-900">Strength Workshops</h4>
                    </div>
                    <p className="text-gray-600">Rediscover your inner strength through guided workshops.</p>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center mb-4">
                      <Shield className="w-6 h-6 text-purple-600 mr-4" />
                      <h4 className="text-lg font-semibold text-gray-900">Safety Planning</h4>
                    </div>
                    <p className="text-gray-600">Clear steps to protect yourself and those you love.</p>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center mb-4">
                      <HandHeart className="w-6 h-6 text-purple-600 mr-4" />
                      <h4 className="text-lg font-semibold text-gray-900">Professional Connections</h4>
                    </div>
                    <p className="text-gray-600">Connect with professionals who can walk beside you as you move forward.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* IWC Modal */}
      <Dialog open={isIWCModalOpen} onOpenChange={setIsIWCModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              The International Women's Connection (IWC) Membership
            </DialogTitle>
            <p className="text-lg font-semibold" style={{ color: 'rgba(127, 81, 244, 1)' }}>
              GLOBAL COMMUNITY
            </p>
          </DialogHeader>
          
          <div className="mt-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <p className="text-xl text-gray-700 leading-relaxed mb-6">
                  A private global community designed for women navigating transitions.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Where you can access curated experiences that transform insight into momentum and expand your perspective through global connections.
                </p>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-8 rounded-2xl">
                <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                  Members receive:
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center mb-4">
                      <Waves className="w-6 h-6 text-purple-600 mr-4" />
                      <h4 className="text-lg font-semibold text-gray-900">Ocean Bloom</h4>
          </div>
                    <p className="text-gray-600">Guided reflection at sea, where conversation and nature combine to spark clarity.</p>
        </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center mb-4">
                      <Calendar className="w-6 h-6 text-purple-600 mr-4" />
                      <h4 className="text-lg font-semibold text-gray-900">Workshops & Gatherings</h4>
                    </div>
                    <p className="text-gray-600">Practical, skill-building sessions that turn insight into momentum.</p>
          </div>
          
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center mb-4">
                      <Globe className="w-6 h-6 text-purple-600 mr-4" />
                      <h4 className="text-lg font-semibold text-gray-900">The International Passport Society</h4>
                    </div>
                    <p className="text-gray-600">Global travel and cultural exchange designed to expand perspective and adaptability.</p>
                  </div>
                </div>
          </div>
        </div>
        </div>
        </DialogContent>
      </Dialog>

      {/* Resource Library Modal */}
      <Dialog open={isResourceModalOpen} onOpenChange={setIsResourceModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Resource Library
            </DialogTitle>
            <p className="text-lg font-semibold" style={{ color: 'rgba(127, 81, 244, 1)' }}>
              KNOWLEDGE BASE
            </p>
          </DialogHeader>
          
          <div className="mt-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <p className="text-xl text-gray-700 leading-relaxed mb-6">
                  A place to find what you need, when you need it.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Comprehensive resources designed to support your journey with practical tools, emergency contacts, and trusted connections.
                </p>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-8 rounded-2xl">
                <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                  Available Resources:
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center mb-4">
                      <BookOpen className="w-6 h-6 text-purple-600 mr-4" />
                      <h4 className="text-lg font-semibold text-gray-900">Mental Health Guides</h4>
                    </div>
                    <p className="text-gray-600">Self-help worksheets and comprehensive mental health guides.</p>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center mb-4">
                      <Phone className="w-6 h-6 text-purple-600 mr-4" />
                      <h4 className="text-lg font-semibold text-gray-900">Crisis Support</h4>
                    </div>
                    <p className="text-gray-600">Crisis hotlines and emergency numbers for immediate support.</p>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center mb-4">
                      <FolderOpen className="w-6 h-6 text-purple-600 mr-4" />
                      <h4 className="text-lg font-semibold text-gray-900">Support Services</h4>
                    </div>
                    <p className="text-gray-600">Local and national support services directory.</p>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center mb-4">
                      <Compass className="w-6 h-6 text-purple-600 mr-4" />
                      <h4 className="text-lg font-semibold text-gray-900">Safety Planning</h4>
                    </div>
                    <p className="text-gray-600">Templates and tools for creating your safety plan.</p>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm md:col-span-2">
                    <div className="flex items-center mb-4">
                      <Link className="w-6 h-6 text-purple-600 mr-4" />
                      <h4 className="text-lg font-semibold text-gray-900">Trusted Organizations</h4>
                    </div>
                    <p className="text-gray-600">Links to trusted organizations and professionals in your area.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Services;